export type ParsedLiveWatchUrl = {
  platform: 'youtube' | 'twitch' | 'kick';
  kind: 'channel' | 'url';
  platformChannelId: string;
  platformLogin: string;
  sourceUrl: string;
};

const TWITCH_RESERVED = new Set([
  'directory',
  'videos',
  'settings',
  'search',
  'p',
  'downloads',
  'jobs',
  'turbo',
  'inventory',
  'wallet',
  'subscriptions',
  'drops',
  'bits',
  'friends',
  'messages',
  'broadcast',
  'dashboard',
  'moderator',
]);

const KICK_RESERVED = new Set([
  'categories',
  'browse',
  'following',
  'search',
  'dashboard',
  'settings',
  'subscriptions',
]);

const emptyIdentity = {
  platformChannelId: '',
  platformLogin: '',
  sourceUrl: '',
};

const coerceUrl = (raw: string): URL | null => {
  const trimmed = raw.trim();
  if (!trimmed || !trimmed.includes('.')) return null;
  const candidates = trimmed.includes('://') ? [trimmed] : [`https://${trimmed}`];
  for (const candidate of candidates) {
    try {
      return new URL(candidate);
    } catch {
      // try the next candidate
    }
  }
  return null;
};

const hostnameOf = (url: URL): string => url.hostname.replace(/^www\./i, '').toLowerCase();

const pathParts = (url: URL): string[] => url.pathname.split('/').filter(Boolean);

const firstSegment = (url: URL): string | undefined => pathParts(url)[0];

const isTwitchLogin = (login: string): boolean =>
  /^[a-zA-Z0-9_]{3,25}$/.test(login) && !TWITCH_RESERVED.has(login.toLowerCase());

const isKickSlug = (slug: string): boolean =>
  /^[a-zA-Z0-9_-]{3,50}$/.test(slug) && !KICK_RESERVED.has(slug.toLowerCase());

const youtubeVideoId = (url: URL): string | null => {
  const host = hostnameOf(url);
  if (host === 'youtu.be') {
    const id = firstSegment(url);
    return id && /^[\w-]{11}$/.test(id) ? id : null;
  }
  if (host !== 'youtube.com' && host !== 'm.youtube.com' && host !== 'music.youtube.com') {
    return null;
  }
  const parts = pathParts(url);
  if (parts[0] === 'watch') {
    const id = url.searchParams.get('v');
    return id && /^[\w-]{11}$/.test(id) ? id : null;
  }
  if ((parts[0] === 'live' || parts[0] === 'embed' || parts[0] === 'shorts') && parts[1]) {
    return /^[\w-]{11}$/.test(parts[1]) ? parts[1] : null;
  }
  return null;
};

const youtubeChannelId = (url: URL): string | null => {
  const host = hostnameOf(url);
  if (host !== 'youtube.com' && host !== 'm.youtube.com') return null;
  const parts = pathParts(url);
  if (parts[0] === 'channel' && parts[1] && /^UC[\w-]{21,}$/.test(parts[1])) {
    return parts[1];
  }
  return null;
};

export const parseLiveWatchUrl = (raw: string): ParsedLiveWatchUrl | null => {
  const url = coerceUrl(raw);
  if (!url) return null;
  const host = hostnameOf(url);

  if (host === 'twitch.tv' || host === 'm.twitch.tv') {
    const login = firstSegment(url);
    if (!login || !isTwitchLogin(login)) return null;
    return {
      platform: 'twitch',
      kind: 'channel',
      ...emptyIdentity,
      platformLogin: login.toLowerCase(),
    };
  }

  if (host === 'kick.com' || host === 'm.kick.com') {
    const slug = firstSegment(url);
    if (!slug || !isKickSlug(slug)) return null;
    return {
      platform: 'kick',
      kind: 'channel',
      ...emptyIdentity,
      platformLogin: slug.toLowerCase(),
    };
  }

  const channelId = youtubeChannelId(url);
  if (channelId) {
    return {
      platform: 'youtube',
      kind: 'channel',
      ...emptyIdentity,
      platformChannelId: channelId,
    };
  }

  if (youtubeVideoId(url)) {
    return {
      platform: 'youtube',
      kind: 'url',
      ...emptyIdentity,
      sourceUrl: url.toString(),
    };
  }

  return null;
};

export const displayLiveWatchUrl = ({
  platform,
  kind,
  platformChannelId,
  platformLogin,
  sourceUrl,
}: {
  platform: string;
  kind: string;
  platformChannelId: string;
  platformLogin: string;
  sourceUrl: string;
}): string => {
  if (kind === 'url' && sourceUrl) return sourceUrl;
  if (platform === 'twitch' && platformLogin) return `https://www.twitch.tv/${platformLogin}`;
  if (platform === 'kick' && platformLogin) return `https://kick.com/${platformLogin}`;
  if (platform === 'youtube' && platformChannelId) {
    return `https://www.youtube.com/channel/${platformChannelId}`;
  }
  return sourceUrl;
};
